import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { useEffect } from 'react'
import { useDispatch } from 'react-redux'

import { saveLocalStorage, loadLocalStorage } from '../../../core/utils'
import {
  claimActiveTab,
  clearActiveTab,
  refreshTabId,
  getTabId,
  SESSION_CHANNEL_NAME,
  SESSION_MESSAGES,
} from '../../../core/utils/tabSession'
import { requests } from '../../../core/ajax'
import { setCombo } from '../../combos/slices'
import { resetAppsData } from '../../applications/slices'
import { feedback } from '../../../core/feedback'

const initialState = {
  isAuthorized: false,
}

const globalSlice = createSlice({
  name: 'global',
  initialState: initialState,
  reducers: {
    setAuthData: (state, { payload }) => payload,
    resetAuthData: () => initialState,
    setCompanyData: (state, { payload }) => {
      state.empresa = payload
    },
  },
})

export const globalReducer = globalSlice.reducer

export const { setAuthData, resetAuthData, setCompanyData } = globalSlice.actions

export const loginUser = (payload) => async (dispatch, getState) => {
  let isRefreshRequest = false
  if (!payload) {
    payload = getState().global
    isRefreshRequest = true
  }

  const { getById, get } = requests

  if (isRefreshRequest) {
    const currentStorage = loadLocalStorage('apins-auth')
    if (currentStorage) {
      payload = {
        ...payload,
        token: currentStorage.token,
        refresh_token: currentStorage.refresh_token,
      }
    }
  }

  const localData = { 'apins-auth': { ...payload } || {} }
  if (localData['apins-auth']?.permissions) {
    delete localData['apins-auth'].permissions
  }
  saveLocalStorage('dsc', localData)
  if (!payload?.user_id) {
    dispatch(resetAuthData())
    return
  }

  const handleForceLogout = () => {
    saveLocalStorage('dsc', {})
    dispatch(resetAuthData())
    setTimeout(window.location.reload(), 1000)
  }

  const loadGremio = async (content) => {
    const { data } = await getById('/instalador', content['user_id']).catch(({ response }) => {
      if (response?.status === 403) {
        handleForceLogout()
      }
      return false
    })
    if (!data) {
      return false
    }

    return { ...content, gremio: data['gremio'], permissions: data['permissions'] }
  }

  const loadComercializadora = async (content) => content

  const loadInstaller = async (content) => {
    const { data: comboData } = await get('/combo_box', {
      params: { parent_id: 'cod_zona_zeus' },
    }).catch(() => {
      return { data: [] }
    })
    const { data } = await getById('/instalador', content['user_id']).catch(({ response }) => {
      if (response?.status === 403) {
        handleForceLogout()
      }
      return false
    })
    if (!data) {
      return false
    }
    let empresa_cc_aa = null
    if (data['empresas_ca_relacionadas']) {
      empresa_cc_aa = data['empresas_ca_relacionadas'].map((empresa) => {
        const comunitat = comboData.filter(
          (el) => parseInt(el.key) === empresa['comunidad_autonoma']
        )[0]
        return {
          ...empresa,
          id_empresa: empresa['id'],
          id_cc_aa: empresa['comunidad_autonoma'],
          nombre_empresa: empresa['nombre'],
          nombre_cc_aa: comunitat ? comunitat.value : 'Unknown',
          gremio_id: empresa['gremio_id'],
          aceptacion_gremio: empresa['aceptacion_gremio'],
          telefono: empresa['telefono'],
        }
      })
    }
    if (data['empresa_id']) {
      await getById('/empresa', data['empresa_id']).then(({ data: empresa }) => {
        data['empresa'] = {
          ...data['empresa'],
          ...empresa,
          cc_aa: empresa.empresas_filiales.map((el) => '' + el.comunidad_autonoma),
        }

        if (!empresa_cc_aa || empresa_cc_aa.length === 0) {
          if (empresa.empresas_filiales) {
            empresa_cc_aa = empresa.empresas_filiales.map((filial) => {
              const comunitat = comboData.filter(
                (el) => parseInt(el.key) === filial['comunidad_autonoma']
              )[0]
              return {
                ...filial,
                id_empresa: filial['id'],
                id_cc_aa: filial['comunidad_autonoma'],
                nombre_empresa: empresa['nombre'],
                nombre_cc_aa: comunitat ? comunitat.value : 'Unknown',
              }
            })
          }
        }
      })
    }

    dispatch(setCombo({ comboId: 'cod_zona_zeus', data: comboData }))

    return {
      ...content,
      permissions: data['permissions'],
      email: data['email'],
      empresa_id: data['empresa_id'],
      empresa: data['empresa'],
      empresa_cc_aa,
    }
  }

  let content = { ...payload, isAuthorized: true }

  switch (payload.role) {
    case 'gremio':
      content = await loadGremio(content)
      break

    case 'comercializadora':
      content = await loadComercializadora(content)
      break

    case 'administrador_prescriptor':
      content = await loadInstaller(content)
      break

    default:
      content = await loadInstaller(content)
  }
  if (!content) {
    return
  }
  dispatch(setAuthData(content))
  claimActiveTab()
}

export const logoutUser = createAsyncThunk('global/logoutUser', async (_, { dispatch }) => {
  try {
    await requests.revokeToken()
    feedback('success', 'Sesión cerrada correctamente')
  } catch (err) {
  } finally {
    localStorage.removeItem('dsc')
    localStorage.removeItem('apins-auth')
    clearActiveTab()
    dispatch(resetAuthData())
    dispatch(resetAppsData())
  }
})

export const checkSessionValidity = () => (dispatch, getState) => {
  const { isAuthorized } = getState().global
  const authData = loadLocalStorage('apins-auth')

  if (isAuthorized && (!authData || !authData.token)) {
    console.debug('Sesión inválida detectada, cerrando sesión local')
    dispatch(resetAuthData())
    dispatch(resetAppsData())
  }
}

export const isSessionValid = () => (_, getState) => {
  const { isAuthorized } = getState().global

  if (!isAuthorized) {
    return false
  }

  const authData = loadLocalStorage('apins-auth')
  return !!(authData && authData.token && authData.refresh_token)
}

export const initializeAuthListeners = () => (dispatch) => {
  refreshTabId()

  const handleStorageChange = (event) => {
    if (event.key === 'dsc') {
      dispatch(checkSessionValidity())
    }
  }

  const handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      dispatch(checkSessionValidity())
    }
  }

  const sessionChannel = new BroadcastChannel(SESSION_CHANNEL_NAME)
  const handleSessionMessage = (event) => {
    if (event.data?.type === SESSION_MESSAGES.SESSION_TAKEN && event.data?.tabId !== getTabId()) {
      feedback('warning', 'Tu sesión fue iniciada en otra pestaña')
      dispatch(resetAuthData())
      dispatch(resetAppsData())
    }
  }
  sessionChannel.addEventListener('message', handleSessionMessage)

  window.addEventListener('storage', handleStorageChange)
  document.addEventListener('visibilitychange', handleVisibilityChange)

  return () => {
    window.removeEventListener('storage', handleStorageChange)
    document.removeEventListener('visibilitychange', handleVisibilityChange)
    sessionChannel.removeEventListener('message', handleSessionMessage)
    sessionChannel.close()
  }
}

export const useAuthStorage = () => {
  const dispatch = useDispatch()

  useEffect(() => {
    const cleanup = dispatch(initializeAuthListeners())

    return cleanup
  }, [dispatch])

  const checkSessionValid = () => {
    return dispatch(isSessionValid())
  }

  return {
    isSessionValid: checkSessionValid,
  }
}
